import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth';

interface ProfileSettings {
  about_me?: string;
  dont_show_scripts?: boolean;
  dont_show_favorites?: boolean;
  dont_show_comments?: boolean;
  default_voice_id?: string | null; // Preferred default voice, pre-selected on /create
}

interface UserPermissions {
  can_customize?: boolean;
  can_create?: boolean;
  private_assets?: boolean;
  private_scripts?: boolean;
  is_admin?: boolean;
  protected_scripts?: boolean;
  can_download?: boolean;
  exclusive_assets?: boolean;
  post_blog?: boolean;
  custom_voices?: number; // Number of custom voices allowed (0 = unavailable, -1 = unlimited)
}

interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  profile_image?: string;
  profile_image_prompt?: string;
  profile_settings?: ProfileSettings;
  tier: number;
  listens: number;
  coins: number;
  no_expire_coins: number;
  user_permissions: UserPermissions;
  email_newsletter: boolean;
  subscription_status: 'active' | 'unpaid' | 'paused' | 'trialing' | 'canceled' | 'failed' | 'incomplete' | 'incomplete_expired' | 'past_due';
  subscription_period?: 'month' | 'year';
  reset_lock_date?: string;
  next_reset_date?: string;
  provider: string;
  created_at: string;
  updated_at: string;
}

interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [isLoading, setIsLoading] = useState(false);

  // Fetch user data when logged in
  const refreshUser = async () => {
    if (!isLoggedIn) {
      setUser(null);
      return;
    }

    setIsLoading(true);
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error fetching user data:', error);
      // If error fetching user, they might be logged out
      setIsLoggedIn(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Listen for authentication state changes from AuthService
  useEffect(() => {
    const handleAuthChange = (isAuthenticated: boolean) => {
      setIsLoggedIn(isAuthenticated);
    };

    // Add listener for auth changes
    authService.addAuthListener(handleAuthChange);

    // Cleanup listener on unmount
    return () => {
      authService.removeAuthListener(handleAuthChange);
    };
  }, []);

  // Initial user fetch when login state changes
  useEffect(() => {
    refreshUser();
  }, [isLoggedIn]);

  // Proactive token expiration monitoring
  useEffect(() => {
    // Check token validity every 60 seconds
    const intervalId = setInterval(() => {
      if (isLoggedIn && !authService.isAuthenticated()) {
        console.warn('Token expired during periodic check, logging out');
        setIsLoggedIn(false);
        setUser(null);
      }
    }, 60000); // Check every 60 seconds

    // Also check on mount if we think we're logged in
    if (isLoggedIn && !authService.isAuthenticated()) {
      console.warn('Token expired on mount, logging out');
      setIsLoggedIn(false);
      setUser(null);
    }

    return () => clearInterval(intervalId);
  }, [isLoggedIn]);

  // Proactively renew the token before it expires, as long as the app is open
  // (including background tabs). Renews on an interval and when the tab regains focus.
  useEffect(() => {
    if (!isLoggedIn) return;

    const REFRESH_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000; // renew when < 2 days remain
    const CHECK_INTERVAL_MS = 30 * 60 * 1000; // check every 30 minutes
    let refreshing = false;

    const maybeRefresh = async () => {
      if (refreshing) return;
      const exp = authService.getTokenExpirationTime();
      if (!exp) return;
      const msLeft = exp - Date.now();
      // Only renew while still valid and inside the renewal window
      if (msLeft > 0 && msLeft < REFRESH_THRESHOLD_MS) {
        refreshing = true;
        try {
          await authService.refreshToken();
        } finally {
          refreshing = false;
        }
      }
    };

    maybeRefresh();
    const intervalId = setInterval(maybeRefresh, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoggedIn]);

  // Custom setter for login state that also refreshes user
  const handleSetIsLoggedIn = (loggedIn: boolean) => {
    setIsLoggedIn(loggedIn);
    if (!loggedIn) {
      setUser(null);
    }
  };

  const value: UserContextType = {
    user,
    isLoggedIn,
    isLoading,
    refreshUser,
    setUser,
    setIsLoggedIn: handleSetIsLoggedIn,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Custom hook specifically for coin operations
export const useCoins = () => {
  const { user, refreshUser } = useUser();
  
  const regularCoins = user?.coins || 0;
  const noExpireCoins = user?.no_expire_coins || 0;
  const totalCoins = regularCoins + noExpireCoins;
  
  return {
    coins: regularCoins, // For backward compatibility
    regularCoins,
    noExpireCoins,
    totalCoins, // Total available coins
    no_expire_coins: noExpireCoins, // For backward compatibility
    refreshCoins: refreshUser, // Call this after any coin operation
  };
};

// Custom hook for checking user permissions
export const usePermissions = () => {
  const { user } = useUser();
  
  const hasPermission = (permission: keyof UserPermissions): boolean => {
    return !!user?.user_permissions?.[permission];
  };

  // Number of custom voices the user may create (0 = none, -1 = unlimited)
  const customVoicesLimit = user?.user_permissions?.custom_voices ?? 0;

  return {
    hasPermission,
    isAdmin: hasPermission('is_admin'),
    canCreate: hasPermission('can_create'),
    canCustomize: hasPermission('can_customize'),
    hasPrivateAssets: hasPermission('private_assets'),
    hasPrivateScripts: hasPermission('private_scripts'),
    hasProtectedScripts: hasPermission('protected_scripts'),
    canDownload: hasPermission('can_download'),
    hasExclusiveAssets: hasPermission('exclusive_assets'),
    customVoicesLimit,
    canUseCustomVoices: customVoicesLimit !== 0, // true when > 0 or unlimited (-1)
  };
}; 